import { useCallback, useMemo, useState } from 'react';
import { Box, Card, CardContent, Chip, Stack, Typography, Tooltip, useTheme, Radio } from '@mui/material';
import { BusinessVoiceEngine, Language, VoiceEngine } from '@/apis/agency/voiceEngines/types';
import ArrowIcon from '@/assets/icons/ArrowIcon';
import Button from '@/components/Common/Button';
import { titleCase } from '@/utils/helperFunctions';
import { useUpdateBusinessVoiceEngines, useUpdateVoiceEngine } from '@/apis/agency/voiceEngines/hook';
import { getBusinessVoiceEngines } from '@/apis/agency/voiceEngines/api';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import { useToast } from '@/hooks/toast';
import VoiceEngineUpdateDetailsModal from './VoiceEngineUpdateDetailsModal';
import VoiceEngineUpdateLoadingModal from './VoiceEngineUpdateLoadingModal';
import VoiceEngineUpdateBusinessesModal from './VoiceEngineUpdateBusinessesModal';

interface VoiceEngineCardProps {
  engine: VoiceEngine;
  selected?: boolean;
  onSelect?: (id: number) => void;
  businessEngineVersion?: string | null;
  showUpdateButton?: boolean;
  isSelectable?: boolean;
  allEngines?: VoiceEngine[];
  businessProfileUpdateContext?: { businessId: number };
}

function normalizeBusinessVoiceEngineRow(row: any, fallbackEngineId: number): BusinessVoiceEngine {
  const rawStatus = String(row?.status ?? row?.account_status ?? 'disabled').toLowerCase();
  return {
    business_id: Number(row?.business_id ?? row?.id),
    business_name: String(row?.business_name ?? row?.name ?? 'Unknown Business'),
    voice_engine_id: Number(row?.voice_engine_id ?? fallbackEngineId),
    status: rawStatus === 'enabled' || rawStatus === 'active' ? 'enabled' : 'disabled'
  };
}

export default function VoiceEngineCard({
  engine,
  selected = false,
  onSelect,
  isSelectable = false,
  allEngines,
  businessEngineVersion,
  businessProfileUpdateContext
}: VoiceEngineCardProps) {
  const theme = useTheme();
  const { showToast, ToastComponent } = useToast();

  const enginesForLabels = allEngines?.length ? allEngines : [engine];

  const visibleLangs = engine?.configuration?.stt?.languages?.slice(0, 3);
  const hiddenLangs = engine?.configuration?.stt?.languages?.slice(3);
  const versionToCompare = isSelectable ? businessEngineVersion : engine?.configuration?.version;
  const latestVersionToCompare = isSelectable ? engine?.configuration?.version : engine?.latest_version;
  const isUpdateAvailable = versionToCompare ? latestVersionToCompare !== versionToCompare : false;
  const { mutateAsync: updateVersion } = useUpdateVoiceEngine();
  const { mutateAsync: updateBusinessVoiceEngines, isPending: isUpdatingBusinesses } = useUpdateBusinessVoiceEngines();

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loadingOpen, setLoadingOpen] = useState(false);
  const [businessesOpen, setBusinessesOpen] = useState(false);
  const [businessRows, setBusinessRows] = useState<BusinessVoiceEngine[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const eligibleBusinesses = useMemo(
    () => businessRows.filter((b) => b.voice_engine_id === engine.id),
    [businessRows, engine.id]
  );

  const eligibleIds = useMemo(() => eligibleBusinesses.map((b) => b.business_id), [eligibleBusinesses]);

  const selectedEligibleCount = useMemo(
    () => eligibleIds.filter((id) => selectedIds.has(id)).length,
    [eligibleIds, selectedIds]
  );

  const allEligibleSelected = eligibleIds.length > 0 && selectedEligibleCount === eligibleIds.length;
  const someEligibleSelected = selectedEligibleCount > 0 && !allEligibleSelected;

  const resetFlow = useCallback(() => {
    setDetailsOpen(false);
    setLoadingOpen(false);
    setBusinessesOpen(false);
    setBusinessRows([]);
    setSelectedIds(new Set());
  }, []);

  const openUpdateFlow = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setDetailsOpen(true);
  }, []);

  const handleDetailsConfirm = useCallback(async () => {
    setDetailsOpen(false);
    setLoadingOpen(true);
    try {
      await updateVersion(engine.id);
      const list =
        (await getBusinessVoiceEngines(engine.id))?.data?.map((row) =>
          normalizeBusinessVoiceEngineRow(row, engine.id)
        ) ?? [];

      const filtered = businessProfileUpdateContext
        ? list.filter((b) => b.business_id === businessProfileUpdateContext.businessId)
        : list;

      setBusinessRows(filtered);
      const initialSelected = new Set(
        filtered.filter((b) => b.voice_engine_id === engine.id).map((b) => b.business_id)
      );
      setSelectedIds(initialSelected);
      setLoadingOpen(false);
      setBusinessesOpen(true);
    } catch (err: any) {
      setLoadingOpen(false);
      const msg = err?.response?.data?.error || err?.message || 'Could not load businesses';
      showToast(msg, 'error');
    }
  }, [businessProfileUpdateContext, engine.id, showToast, updateVersion]);

  const handleToggle = useCallback((businessId: number, eligible: boolean) => {
    if (!eligible) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(businessId)) next.delete(businessId);
      else next.add(businessId);
      return next;
    });
  }, []);

  const handleToggleAllEligible = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (eligibleIds.length === 0) return next;
      const allOn = eligibleIds.every((id) => next.has(id));
      if (allOn) {
        eligibleIds.forEach((id) => next.delete(id));
      } else {
        eligibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [eligibleIds]);

  const handleApplyUpdate = useCallback(
    async (ids?: number[]) => {
      const selectedBusinessIds = ids ?? eligibleIds.filter((id) => selectedIds.has(id));
      if (!selectedBusinessIds) return;
      try {
        await updateBusinessVoiceEngines({ availableVoiceEngineId: engine.id, businessIds: selectedBusinessIds });
        showToast(`Updated ${selectedBusinessIds.length} business(es) to version ${engine.latest_version}`, 'success');
        resetFlow();
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Update failed';
        showToast(msg, 'error');
      }
    },
    [eligibleIds, engine.id, engine.latest_version, resetFlow, selectedIds, showToast, updateBusinessVoiceEngines]
  );

  const targetVersionLabel = engine.latest_version;

  return (
    <>
      <Card
        variant='outlined'
        onClick={() => isSelectable && onSelect?.(engine.id)}
        sx={{
          borderRadius: 2,
          border: isSelectable ? 2 : null,
          borderColor: selected ? theme.palette.primary.main : 'divider',
          height: '100%',
          cursor: isSelectable ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          '&:hover': isSelectable ? { borderColor: theme.palette.primary.main } : {}
        }}
      >
        <CardContent>
          <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1}>
            <Stack direction='row' alignItems='center' spacing={0.5}>
              <Typography fontSize={18} fontWeight='500'>
                {engine?.configuration?.friendly_name}
              </Typography>
              {isSelectable ? getTooltip(engine?.configuration?.description) : ''}
            </Stack>
            {!isSelectable && engine?.deprecated && (
              <Typography
                paddingX='12px'
                paddingY='4px'
                fontSize='14px'
                borderRadius='100px'
                fontWeight={500}
                color='#DA0808'
                bgcolor='#FFF2F2'
              >
                Deprecated
              </Typography>
            )}
            {isSelectable && <Radio checked={selected} color='primary' />}
          </Stack>

          <Typography fontSize={13} color='text.secondary' mb={1}>
            Version {selected && businessEngineVersion ? businessEngineVersion : engine?.configuration?.version}
            {isUpdateAvailable && !isSelectable && (
              <>
                <Typography color='primary' component='span' sx={{ mx: 0.5 }}>
                  <ArrowIcon />
                </Typography>
                <Typography
                  fontSize={14}
                  component='span'
                  color='primary'
                  fontWeight={400}
                  sx={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openUpdateFlow(e);
                  }}
                >
                  {engine.latest_version} available
                </Typography>
              </>
            )}
            {isUpdateAvailable && isSelectable && businessProfileUpdateContext && selected && businessEngineVersion && (
              <>
                <Typography color='primary' component='span' sx={{ mx: 0.5 }}>
                  <ArrowIcon />
                </Typography>
                <Typography
                  fontSize={14}
                  component='span'
                  color='primary'
                  fontWeight={400}
                  sx={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openUpdateFlow(e);
                  }}
                >
                  {engine.latest_version} available
                </Typography>
              </>
            )}
          </Typography>

          {!isSelectable ? (
            <Typography fontSize='14px' fontWeight={400} my='12px' lineHeight={1.5}>
              {engine?.configuration?.description}
            </Typography>
          ) : (
            ''
          )}

          <Stack direction='row' justifyContent='space-between' mb={1}>
            <Box>
              <Typography variant='caption' color='text.secondary' display='block'>
                Conversation Mode
              </Typography>
              <Typography variant='body2'>
                {engine?.configuration?.stt?.language_support === 'monolingual' ? 'Single Language' : 'Multi-Language'}
              </Typography>
            </Box>
            <Box>
              <Typography variant='caption' color='text.secondary' display='block'>
                Specialization
              </Typography>
              <Typography variant='body2'>{titleCase(engine?.configuration?.specialization)}</Typography>
            </Box>
          </Stack>

          <Box>
            <Typography variant='caption' color='text.secondary'>
              Supported Languages
            </Typography>

            <Stack direction='row' flexWrap='wrap' gap={1} mt={1}>
              {visibleLangs?.map((lang: Language) => (
                <Chip
                  key={lang.code}
                  label={lang.name}
                  size='small'
                  sx={{
                    fontSize: '12px',
                    backgroundColor: '#F8F9FA'
                  }}
                />
              ))}

              {hiddenLangs?.length > 0 && (
                <Tooltip
                  arrow
                  placement='top'
                  title={
                    <Box display='flex' flexWrap='wrap' gap={0.6} maxWidth={200}>
                      {hiddenLangs.map((lang: Language, idx: number) => (
                        <Chip
                          key={idx}
                          label={lang.name}
                          size='small'
                          sx={{
                            fontSize: '12px',
                            backgroundColor: '#F8F9FA'
                          }}
                        />
                      ))}
                    </Box>
                  }
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'white',
                        color: theme.palette.text.primary,
                        boxShadow: theme.shadows[2],
                        p: 1,
                        borderRadius: 1
                      }
                    },
                    arrow: {
                      sx: { color: 'white' }
                    }
                  }}
                >
                  <Chip
                    label={`+${hiddenLangs?.length}`}
                    size='small'
                    sx={{
                      color: theme.palette.primary.main,
                      cursor: 'pointer',
                      backgroundColor: theme.palette.grey[100]
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Box>
        </CardContent>

        {isUpdateAvailable && !isSelectable && (
          <Box p={2} pt={0}>
            <Button isPending={false} fullWidth onClick={(e) => openUpdateFlow(e)}>
              Update to {engine?.latest_version}
            </Button>
          </Box>
        )}

        {isUpdateAvailable && isSelectable && businessProfileUpdateContext && selected && (
          <Box p={2} pt={0} onClick={(e) => e.stopPropagation()}>
            <Button
              isPending={false}
              fullWidth
              onClick={() => handleApplyUpdate([businessProfileUpdateContext?.businessId])}
            >
              Update to {engine?.latest_version}
            </Button>
          </Box>
        )}
      </Card>

      <VoiceEngineUpdateDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        engine={engine}
        onConfirm={handleDetailsConfirm}
      />

      <VoiceEngineUpdateLoadingModal
        open={loadingOpen}
        onClose={() => {
          setLoadingOpen(false);
        }}
        engine={engine}
      />

      <VoiceEngineUpdateBusinessesModal
        open={businessesOpen}
        onClose={resetFlow}
        engine={engine}
        businesses={businessRows}
        allEngines={enginesForLabels}
        selectedIds={selectedIds}
        onToggle={handleToggle}
        onToggleAllEligible={handleToggleAllEligible}
        onConfirm={handleApplyUpdate}
        isUpdating={isUpdatingBusinesses}
        targetVersionLabel={targetVersionLabel}
        allEligibleSelected={allEligibleSelected}
        someEligibleSelected={someEligibleSelected}
        title={businessProfileUpdateContext ? 'Apply engine update?' : 'Update All Businesses?'}
        subtitle={
          businessProfileUpdateContext
            ? `Apply version ${targetVersionLabel} to this business? You can review status before confirming.`
            : undefined
        }
      />

      {ToastComponent}
    </>
  );
}

const getTooltip = (content: string) => {
  if (!content) return;

  return (
    <Box display='flex' flexDirection='row' alignItems='center' gap={0.5}>
      <Tooltip
        title={
          <Typography lineHeight={1.5} fontSize={12} px={0.5}>
            {content}
          </Typography>
        }
        placement='top'
        arrow
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: 'black',
              color: 'white',
              boxShadow: 3,
              fontSize: '0.85rem',
              borderRadius: 1,
              p: 0.5,
              lineHeight: 1.2
            }
          },
          arrow: {
            sx: { color: 'black' }
          }
        }}
      >
        <Box mt={0.7}>
          <IoIosInformationCircleOutline cursor='pointer' size={18} />
        </Box>
      </Tooltip>
    </Box>
  );
};
